<script setup lang="ts">
import GaiaText from '../base/GaiaText.vue';
import { gaiaAccent, gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** The analysis name. Pass #default instead to swap in a rename input. */
  label: {
    type: String,
    default: '',
  },
  active: {
    type: Boolean,
    default: false,
  },
  /**
   * A data-room file drag is over this tab — dropping starts a playbook run,
   * so the tab has to say it will accept one.
   */
  dragOver: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ select: [] }>();

const border = gaiaNeutral.border;
const surface = gaiaNeutral.surface;
const text = gaiaNeutral.text;
const activeBorder = gaiaAccent.border;
const activeSurface = gaiaAccent.surface;
const activeText = gaiaAccent.strong;
const dropRing = gaiaAccent.base;
const labelSize = gaiaFontSize.dense;
const semibold = String(gaiaFontWeight.semibold);
</script>

<template>
  <!-- One analysis tab in the workspace's analysis pane (Review Projects
       mock): the name, then the tab's own controls in #actions (settings,
       remove). Accent-tinted while active.
       Bespoke rather than n-tabs type="card": these tabs are a drop target for
       data-room files, and naive's tab head gives no element to hang the drag
       handlers or the drop ring on. -->
  <div
    class="gaia-analysis-tab"
    :class="{ active: props.active, 'drag-over': props.dragOver }"
    role="tab"
    tabindex="0"
    :aria-selected="props.active"
    @click="emit('select')"
    @keydown.enter="emit('select')"
    @keydown.space.prevent="emit('select')"
  >
    <slot>
      <gaia-text
        truncate
        :size="'dense'"
        :style="{ fontWeight: semibold, minWidth: 0 }"
        :title="props.label"
      >
        {{ props.label }}
      </gaia-text>
    </slot>
    <span v-if="$slots.actions" class="actions" @click.stop><slot name="actions" /></span>
  </div>
</template>

<style scoped>
.gaia-analysis-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 260px;
  height: 30px;
  padding: 0 8px 0 10px;
  border: 1px solid v-bind(border);
  border-radius: 8px;
  background: v-bind(surface);
  color: v-bind(text);
  font-size: v-bind(labelSize);
  cursor: pointer;
}

.gaia-analysis-tab.active {
  border-color: v-bind(activeBorder);
  background: v-bind(activeSurface);
  color: v-bind(activeText);
}

/* Ring rather than a border swap: the border already carries active state. */
.gaia-analysis-tab.drag-over {
  outline: 2px dashed v-bind(dropRing);
  outline-offset: 2px;
}

.actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: 2px;
}
</style>
